/**
 * BottomTabBar - Mobile
 * 
 * 5 tabs, safe-area aware (portrait AND landscape).
 * Professional, calm, touch-friendly.
 */

import React from 'react';
import { View, Pressable, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { color, text, space, touch, icon } from '../../../shared/tokens/mobile';
import { NavItem, MOBILE_TAB_ITEMS } from './NavConfig';

// =============================================================================
// TYPES
// =============================================================================

export interface BottomTabBarProps {
  activeTabId: string;
  onTabPress: (item: NavItem) => void;
  activityBadgeCount?: number;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function BottomTabBar({
  activeTabId,
  onTabPress,
  activityBadgeCount = 0,
}: BottomTabBarProps) {
  const insets = useSafeAreaInsets();

  return (
    <View
      style={[
        styles.container,
        {
          paddingBottom: insets.bottom,
          paddingLeft: insets.left,
          paddingRight: insets.right,
        },
      ]}
    >
      {MOBILE_TAB_ITEMS.map((item) => {
        const isActive = activeTabId === item.id;
        const showBadge = item.id === 'activity' && activityBadgeCount > 0;

        return (
          <Pressable
            key={item.id}
            onPress={() => onTabPress(item)}
            style={({ pressed }) => [
              styles.tab,
              pressed && styles.tabPressed,
            ]}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive }}
            accessibilityLabel={item.label}
          >
            <View style={styles.iconContainer}>
              <Ionicons
                name={isActive ? item.iconActive : item.icon}
                size={icon.lg}
                color={isActive ? color.primary : color.textMuted}
              />
              {showBadge && (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>
                    {activityBadgeCount > 9 ? '9+' : activityBadgeCount}
                  </Text>
                </View>
              )}
            </View>
            <Text
              style={[
                styles.label,
                { color: isActive ? color.primary : color.textMuted },
                isActive && styles.labelActive,
              ]}
              numberOfLines={1}
            >
              {item.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

// =============================================================================
// STYLES - ALL FROM TOKENS
// =============================================================================

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: color.surface,
    borderTopWidth: 1,
    borderTopColor: color.border,
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: touch.recommended,
    paddingTop: space.sm,
    paddingBottom: space.xs,
  },
  tabPressed: {
    opacity: 0.7,
  },
  iconContainer: {
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: -space.xs,
    right: -space.sm,
    minWidth: space.md,
    height: space.md,
    borderRadius: space.sm,
    backgroundColor: color.error,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: space.xs,
  },
  badgeText: {
    fontSize: text.tabLabel.fontSize,
    fontWeight: text.tabLabel.fontWeight,
    color: color.textInverse,
  },
  label: {
    fontSize: text.tabLabel.fontSize,
    fontWeight: text.tabLabel.fontWeight,
    marginTop: space.xs,
  },
  labelActive: {
    fontWeight: '600' as const,
  },
});

export default BottomTabBar;
